import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import DocStatusTag from '_components/utils/DocStatusTag';
import CustomPagination from '_components/utils/Pagingation/CustomPagination';
import Avatar from './Avatar';
import CancelLayoffModal from './CancelLayoffModal';
import HistoryModal from './HistoryModal';
import ReassignSignerModal from './ReassignSignerModal';
import RecordRefusalModal from './RecordRefusalModal';
import VoidLayoffModal from './VoidLayoffModal';
import { DateInput } from './FormControls';
import {
  LAYOFF_STATUSES,
  getLayoffStatus,
  useDownloadPdf,
  useLayoffList,
  useRemindLayoff,
  useResendEmail,
} from './useLayoff';
import styles from './layoff.module.scss';

const PER_PAGE = 20;

// Cards shown above the table; clicking one filters the list by that status
const CARD_STATUSES = [1, 2, 9, 3, 10];

const SORTABLE = [
  { key: 'employee', label: 'Employee' },
  { key: 'crew', label: 'Crew' },
  { key: null, label: 'Signer' },
  { key: 'sent', label: 'Sent' },
  { key: 'effective_date', label: 'Last day worked' },
  { key: 'status', label: 'Status' },
];

const formatDate = (value) =>
  value ? moment(value).format('MMM D, YYYY') : '—';

const RowActions = ({
  layoff,
  onRemind,
  reminding,
  onResend,
  resending,
  onDownload,
  downloading,
  onOpen,
  canManage,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const pick = (action) => {
    setOpen(false);
    onOpen(action, layoff);
  };

  // Close the menu on a click anywhere outside it (including another row's ⋯) or on Escape,
  // so only one menu is open at a time
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const closeOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    // Escape hands focus back to the ⋯ button, so a keyboard user keeps their place
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  // Reviewers and managers can both resend a failed email to Payroll and HR
  const resendButton = layoff.can.resend_email && (
    <button
      type='button'
      className={`button ${styles.small_button}`}
      onClick={() => onResend(layoff.hash)}
      disabled={resending}
    >
      Resend email
    </button>
  );

  // The finalized form, once the lay-off has been finalized (kept after a void for audit)
  const downloadButton = layoff.can.download_pdf && (
    <button
      type='button'
      className={`button outlined ${styles.small_button}`}
      onClick={() =>
        onDownload({ hash: layoff.hash, employeeName: layoff.employee.name })
      }
      disabled={downloading}
    >
      Download PDF
    </button>
  );

  if (!canManage) {
    return (
      <div className={styles.row_actions}>
        {layoff.can.review && (
          <Link
            to={`/layoff/review/${layoff.hash}`}
            className={`button ${styles.small_button}`}
          >
            Review
          </Link>
        )}
        {resendButton}
        {downloadButton}
        <button
          type='button'
          className={`button outlined ${styles.small_button}`}
          onClick={() => onOpen('history', layoff)}
        >
          History
        </button>
      </div>
    );
  }

  return (
    <div className={styles.row_actions}>
      {layoff.can.review && (
        <Link
          to={`/layoff/review/${layoff.hash}`}
          className={`button ${styles.small_button}`}
        >
          Review
        </Link>
      )}
      {resendButton}
      {layoff.can.remind && (
        <button
          type='button'
          className={`button outlined ${styles.small_button}`}
          onClick={() => onRemind(layoff.hash)}
          disabled={reminding}
        >
          Remind
        </button>
      )}
      <div className={styles.menu} ref={menuRef}>
        <button
          ref={toggleRef}
          type='button'
          className={styles.menu_toggle}
          aria-label={`More actions for ${layoff.employee.name}`}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span aria-hidden='true'>⋯</span>
        </button>
        {open && (
          <ul className={styles.menu_list}>
            <li>
              <button type='button' onClick={() => pick('history')}>
                View history
              </button>
            </li>
            {layoff.can.download_pdf && (
              <li>
                <button
                  type='button'
                  onClick={() => {
                    setOpen(false);
                    onDownload({
                      hash: layoff.hash,
                      employeeName: layoff.employee.name,
                    });
                  }}
                  disabled={downloading}
                >
                  Download PDF
                </button>
              </li>
            )}
            {layoff.can.reassign_signer && (
              <li>
                <button type='button' onClick={() => pick('reassign')}>
                  Reassign signer
                </button>
              </li>
            )}
            {layoff.can.record_refusal && (
              <li>
                <button type='button' onClick={() => pick('refusal')}>
                  Record refused to sign
                </button>
              </li>
            )}
            {layoff.can.cancel && (
              <li>
                <button
                  type='button'
                  className={styles.danger}
                  onClick={() => pick('cancel')}
                >
                  Cancel lay-off
                </button>
              </li>
            )}
            {layoff.can.void && (
              <li>
                <button
                  type='button'
                  className={styles.danger}
                  onClick={() => pick('void')}
                >
                  Void notice
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

const LayoffTracker = ({ canManage = true, canSend = canManage }) => {
  const [keywordInput, setKeywordInput] = useState('');
  const [params, setParams] = useState({
    page: 1,
    per_page: PER_PAGE,
    status: '',
    keyword: '',
    date_from: '',
    date_to: '',
    sort: 'sent',
    order: 'desc',
  });
  const [modal, setModal] = useState({ type: null, layoff: null });

  // Sent date range; an end date before the start date is ignored
  const rangeInvalid =
    !!params.date_from && !!params.date_to && params.date_to < params.date_from;
  const query = rangeInvalid ? { ...params, date_to: '' } : params;
  const { data, isLoading, isFetching, isError } = useLayoffList(query);

  const remind = useRemindLayoff();
  const resend = useResendEmail();
  const download = useDownloadPdf();

  // In-flight row actions, keyed "action:hash", so a request disables only its own row's button
  const [pending, setPending] = useState({});
  const runForRow = (mutation, action) => (variables) => {
    const hash = typeof variables === 'string' ? variables : variables.hash;
    const key = `${action}:${hash}`;
    setPending((prev) => ({ ...prev, [key]: true }));
    mutation.mutate(variables, {
      onSettled: () =>
        setPending((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        }),
    });
  };
  const isPending = (action, hash) => !!pending[`${action}:${hash}`];
  const onRemind = runForRow(remind, 'remind');
  const onResend = runForRow(resend, 'resend');
  const onDownload = runForRow(download, 'download');

  // Search after the user stops typing for a second
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams((prev) =>
        prev.keyword === keywordInput.trim()
          ? prev
          : { ...prev, keyword: keywordInput.trim(), page: 1 },
      );
    }, 1000);
    return () => clearTimeout(timer);
  }, [keywordInput]);

  const rows = data?.data?.data ?? [];
  const lastPage = data?.data?.last_page ?? 1;
  const counts = data?.additional_data?.counts ?? {};

  const setStatus = (status) =>
    setParams((prev) => ({ ...prev, status, page: 1 }));

  const setDate = (key) => (value) =>
    setParams((prev) => ({ ...prev, [key]: value, page: 1 }));

  const sortBy = (key) =>
    setParams((prev) => ({
      ...prev,
      sort: key,
      order: prev.sort === key && prev.order === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));

  const openModal = (type, layoff) => setModal({ type, layoff });
  const closeModal = () => setModal({ type: null, layoff: null });

  const statusTag = (layoff) => {
    const status = getLayoffStatus(layoff.status);
    return (
      <DocStatusTag
        status={layoff.status_label}
        bgColor={status.bg}
        fontColor={status.fg}
        className={styles.status_tag}
      />
    );
  };

  return (
    <div className='activity-log'>
      <div className='header'>
        <div className='report_tbl_head sold_rpt_mbl_flx'>
          <h4 className='graph_header'>Lay-off documents</h4>
          {canSend && (
            <div className='buttons'>
              <Link to='/layoff/send' className='button'>
                Send new
              </Link>
            </div>
          )}
        </div>
      </div>
      <div className='white'>
        <div className={`inner_container col-lg-12 ${styles.tracker}`}>
          <div className={styles.cards}>
            {CARD_STATUSES.map((value) => {
              const status = getLayoffStatus(value);
              const active = String(params.status) === String(value);
              return (
                <button
                  type='button'
                  key={value}
                  className={`${styles.card} ${active ? styles.card_active : ''}`}
                  aria-pressed={active}
                  onClick={() => setStatus(active ? '' : value)}
                >
                  <span>{status.label}</span>
                  <strong>{counts[value] ?? 0}</strong>
                </button>
              );
            })}
          </div>

          <div className={styles.filters}>
            <div className={styles.filter_field}>
              <label htmlFor='layoff-search' className={styles.field_label}>
                Search
              </label>
              <input
                id='layoff-search'
                type='search'
                className={styles.input}
                value={keywordInput}
                onChange={(event) => setKeywordInput(event.target.value)}
                placeholder='Search employee or crew'
              />
            </div>
            <div className={styles.filter_field}>
              <label htmlFor='layoff-status' className={styles.field_label}>
                Status
              </label>
              <select
                id='layoff-status'
                className={styles.select}
                value={params.status}
                onChange={(event) =>
                  setStatus(
                    event.target.value ? Number(event.target.value) : '',
                  )
                }
              >
                <option value=''>All statuses</option>
                {LAYOFF_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filter_field}>
              <label htmlFor='layoff-date-from' className={styles.field_label}>
                Sent from
              </label>
              <DateInput
                id='layoff-date-from'
                value={params.date_from}
                max={params.date_to || undefined}
                onChange={setDate('date_from')}
              />
            </div>
            <div className={styles.filter_field}>
              <label htmlFor='layoff-date-to' className={styles.field_label}>
                Sent to
              </label>
              <DateInput
                id='layoff-date-to'
                invalid={rangeInvalid}
                describedBy={rangeInvalid ? 'layoff-date-error' : undefined}
                value={params.date_to}
                min={params.date_from || undefined}
                onChange={setDate('date_to')}
              />
              {rangeInvalid && (
                <span id='layoff-date-error' className={styles.error_text}>
                  The end date is before the start date.
                </span>
              )}
            </div>
          </div>

          <div className={styles.table_wrap}>
            <table
              className={`mobile_hide report_list_width_full ${styles.table}`}
            >
              <thead>
                <tr>
                  {SORTABLE.map((column) => (
                    <th key={column.label} scope='col'>
                      {column.key ? (
                        <button
                          type='button'
                          className={styles.sort_button}
                          onClick={() => sortBy(column.key)}
                        >
                          {column.label}
                          {params.sort === column.key && (
                            <span aria-hidden='true'>
                              {params.order === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  ))}
                  <th scope='col' className={styles.actions_col}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((layoff) => (
                  <tr key={layoff.hash}>
                    <td>
                      <div className={styles.person}>
                        <Avatar
                          photo={layoff.employee.profile_photo}
                          name={layoff.employee.name}
                        />
                        <span>{layoff.employee.name}</span>
                      </div>
                    </td>
                    <td>{layoff.crew?.name ?? 'No crew'}</td>
                    <td>
                      {layoff.signer || '—'}
                      {layoff.signer_chosen && (
                        <span className={styles.muted}> (chosen)</span>
                      )}
                      {layoff.signer_missing && (
                        <div className={styles.signer_missing}>
                          No one can sign. Reassign the signer.
                        </div>
                      )}
                    </td>
                    <td>{formatDate(layoff.sent_at)}</td>
                    <td>{formatDate(layoff.effective_date)}</td>
                    <td>
                      {statusTag(layoff)}
                      {layoff.note && (
                        <div className={styles.note}>{layoff.note}</div>
                      )}
                    </td>
                    <td>
                      <RowActions
                        layoff={layoff}
                        onRemind={onRemind}
                        reminding={isPending('remind', layoff.hash)}
                        onResend={onResend}
                        resending={isPending('resend', layoff.hash)}
                        onDownload={onDownload}
                        downloading={isPending('download', layoff.hash)}
                        onOpen={openModal}
                        canManage={canManage}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className={styles.mobile_list}>
              {rows.map((layoff) => (
                <div key={layoff.hash} className={styles.mobile_card}>
                  <div className={styles.person}>
                    <Avatar
                      photo={layoff.employee.profile_photo}
                      name={layoff.employee.name}
                    />
                    <strong>{layoff.employee.name}</strong>
                  </div>
                  {statusTag(layoff)}
                  <dl>
                    <dt>Crew</dt>
                    <dd>{layoff.crew?.name ?? 'No crew'}</dd>
                    <dt>Signer</dt>
                    <dd>
                      {layoff.signer || '—'}
                      {layoff.signer_missing && (
                        <div className={styles.signer_missing}>
                          No one can sign. Reassign the signer.
                        </div>
                      )}
                    </dd>
                    <dt>Sent</dt>
                    <dd>{formatDate(layoff.sent_at)}</dd>
                    <dt>Last day worked</dt>
                    <dd>{formatDate(layoff.effective_date)}</dd>
                  </dl>
                  {layoff.note && (
                    <div className={styles.note}>{layoff.note}</div>
                  )}
                  <RowActions
                    layoff={layoff}
                    onRemind={onRemind}
                    reminding={isPending('remind', layoff.hash)}
                    onResend={onResend}
                    resending={isPending('resend', layoff.hash)}
                    onDownload={onDownload}
                    downloading={isPending('download', layoff.hash)}
                    onOpen={openModal}
                    canManage={canManage}
                  />
                </div>
              ))}
            </div>

            {isLoading && (
              <div className={styles.empty}>
                <img
                  alt='Loading'
                  src={require('../assets/images/loader.gif').default}
                />
              </div>
            )}
            {isError && (
              <div className={styles.empty}>
                Lay-off documents could not be loaded. Please refresh the page.
              </div>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <div className={styles.empty}>
                {params.status ||
                params.keyword ||
                params.date_from ||
                params.date_to
                  ? 'No lay-off documents match these filters.'
                  : 'No lay-off documents have been sent yet.'}
              </div>
            )}
          </div>

          {lastPage > 1 && (
            <CustomPagination
              forcePage={params.page - 1}
              pageCount={lastPage}
              pageRangeDisplayed={5}
              onPageChange={({ selected }) =>
                setParams((prev) => ({ ...prev, page: selected + 1 }))
              }
            />
          )}
          {isFetching && !isLoading && (
            <span className={styles.muted} role='status'>
              Updating...
            </span>
          )}
        </div>
      </div>

      <HistoryModal
        layoff={modal.type === 'history' ? modal.layoff : null}
        onClose={closeModal}
      />
      <ReassignSignerModal
        layoff={modal.type === 'reassign' ? modal.layoff : null}
        onClose={closeModal}
      />
      <CancelLayoffModal
        layoff={modal.type === 'cancel' ? modal.layoff : null}
        onClose={closeModal}
      />
      <RecordRefusalModal
        layoff={modal.type === 'refusal' ? modal.layoff : null}
        onClose={closeModal}
      />
      <VoidLayoffModal
        layoff={modal.type === 'void' ? modal.layoff : null}
        onClose={closeModal}
      />
    </div>
  );
};

export default LayoffTracker;
